import { HiDownload } from 'react-icons/hi';
import WidgetWrapper from '../WidgetWrapper';
import { DashboardP1Insights } from '../../types/dashboard.types';
import { CurrencyFormat } from '../shared/Formats';
import { downloadCsv } from '../../utils/csvDownload';

type DashboardRevenueQualityWidgetProps = {
  insights: DashboardP1Insights | null;
  compareInsights?: DashboardP1Insights | null;
  compareLabel?: string;
  loading?: boolean;
};

export default function DashboardRevenueQualityWidget({
  insights,
  compareInsights = null,
  compareLabel = 'Compare',
  loading = false,
}: DashboardRevenueQualityWidgetProps) {
  const current = insights?.revenueQuality;
  const compare = compareInsights?.revenueQuality;

  const handleDownload = () => {
    const freqMix = current?.frequencyMix ?? [];
    if (!freqMix.length) return;
    downloadCsv('revenue-quality-frequency-mix', freqMix.map((item) => {
      const compareItem = (compare?.frequencyMix ?? []).find((c) => c.frequency === item.frequency);
      return {
        Frequency: item.frequency.replaceAll('_', ' '),
        Count: item.total,
        'Share %': `${item.percentage.toFixed(2)}%`,
        ...(compareItem ? { 'Compare Count': compareItem.total, 'Compare Share %': `${compareItem.percentage.toFixed(2)}%` } : {}),
      };
    }));
  };

  return (
    <WidgetWrapper
      title="Revenue Quality"
      titleStyle="w-full px-4 py-3 rounded-xl bg-primary-10/80 dark:bg-slate-800/80 text-sm uppercase tracking-[0.18em] font-semibold text-gray-700 dark:text-gray-200"
      className="h-full w-full p-4 md:p-5 bg-gradient-to-br from-violet-50/80 via-white/75 to-fuchsia-100/75 dark:from-violet-900/20 dark:via-slate-900/78 dark:to-fuchsia-900/18 border-violet-200/50 dark:border-violet-400/15"
    >
      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl border border-white/60 bg-white/60 dark:border-white/10 dark:bg-slate-900/45" />
      ) : (
        <div className="space-y-3 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <MetricCard label="New donors" value={current?.donorComposition.newDonors ?? 0} compareValue={compare?.donorComposition.newDonors} compareLabel={compareLabel} />
            <MetricCard label="Returning donors" value={current?.donorComposition.returningDonors ?? 0} compareValue={compare?.donorComposition.returningDonors} compareLabel={compareLabel} />
            <MetricCard label="Avg donation" value={CurrencyFormat(current?.donationValue.avgDonationAmount ?? 0)} compareValue={compare ? CurrencyFormat(compare.donationValue.avgDonationAmount) : undefined} compareLabel={compareLabel} />
            <MetricCard label="Median donation" value={CurrencyFormat(current?.donationValue.medianDonationAmount ?? 0)} compareValue={compare ? CurrencyFormat(compare.donationValue.medianDonationAmount) : undefined} compareLabel={compareLabel} />
          </div>

          <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 overflow-hidden">
            <div className="flex justify-end px-3 pt-2">
              <button
                onClick={handleDownload}
                disabled={!(current?.frequencyMix?.length)}
                title="Download CSV"
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-semibold text-violet-700 transition-colors hover:bg-violet-100 dark:text-violet-300 dark:hover:bg-violet-900/30 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <HiDownload className="h-4 w-4" />
                <span>Download CSV</span>
              </button>
            </div>
            <table className="w-full table-auto border-collapse">
              <thead className="bg-primary-50/80 dark:bg-slate-800/80">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Frequency</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Current</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Compare</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Share</th>
                </tr>
              </thead>
              <tbody>
                {(current?.frequencyMix ?? []).map((item) => {
                  const compareItem = (compare?.frequencyMix ?? []).find((c) => c.frequency === item.frequency);
                  return (
                    <tr key={item.frequency} className="border-t border-gray-200/60 dark:border-white/10">
                      <td className="px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100">{item.frequency.replaceAll('_', ' ')}</td>
                      <td className="px-3 py-2 text-sm text-right font-semibold text-gray-900 dark:text-gray-100">{item.total.toLocaleString()}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{compareItem ? compareItem.total.toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{item.percentage.toFixed(2)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
}

type MetricCardProps = {
  label: string;
  value: string | number;
  compareValue?: string | number;
  compareLabel: string;
};

function MetricCard({ label, value, compareValue, compareLabel }: MetricCardProps) {
  return (
    <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</p>
      {compareValue !== undefined && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">{compareLabel}: {compareValue}</p>
      )}
    </div>
  );
}
